let elementName = `nss-blog-filters`;
if (typeof customElements.get(elementName) === `undefined`) {
  class NSSBlogFilters extends HTMLElement {
    constructor() {
      super();

      this.debug = false;
      this.isLoading = false;
    }

    connectedCallback() {
      if (this.debug) console.log(`[NSS][Blog Filters] Connected.`);

      this.addEventListener(`click`, this.handleClick.bind(this));
      window.addEventListener(`popstate`, this.handlePopState.bind(this));
      this.initSticky();
      this.initScrollbar();
    }

    // Drives the custom scrollbar-style pagination under the tags row
    // (styled in nss-blog-filters.scss to match the swiper-scrollbar used
    // on the site's sliders). Only shown when the tags row actually
    // overflows — a short tag list stays plain, on either breakpoint.
    initScrollbar() {
      const wrap = this.querySelector(`[data-tags-scrollbar]`);
      const tags = wrap ? wrap.querySelector(`.nss-blog-filters__tags`) : null;
      const thumb = wrap ? wrap.querySelector(`[data-tags-scrollbar-thumb]`) : null;
      if (!wrap || !tags || !thumb) return;

      const update = () => {
        // +1 tolerance: sub-pixel layout rounding can otherwise report a
        // scrollWidth a fraction over clientWidth for a row that doesn't
        // actually scroll, flashing the bar on for a no-op.
        const scrollable = tags.scrollWidth > tags.clientWidth + 1;
        wrap.classList.toggle(`nss-blog-filters__tags-wrap--scrollable`, scrollable);
        if (!scrollable) return;

        const widthRatio = tags.clientWidth / tags.scrollWidth;
        const maxScroll = tags.scrollWidth - tags.clientWidth;
        const scrollRatio = maxScroll > 0 ? tags.scrollLeft / maxScroll : 0;

        thumb.style.width = `${widthRatio * 100}%`;
        thumb.style.left = `${scrollRatio * (100 - widthRatio * 100)}%`;
      };

      tags.addEventListener(`scroll`, update, { passive: true });
      window.addEventListener(`resize`, update);
      update();
    }

    // A sentinel placed right before this element flips .is-stuck via
    // IntersectionObserver right as it scrolls under the header — same
    // trigger technique as the PLP's sticky filters
    // (nss-collection-groups.liquid), but .is-stuck here ALSO drives
    // position: fixed itself (see nss-blog-filters.scss for why plain
    // position: sticky can't work for this section), so the spacer has to
    // be sized to the bar's current height right before that switch —
    // otherwise the page content jumps up by the bar's height the instant
    // it leaves normal flow.
    initSticky() {
      if (!this.hasAttribute(`data-sticky-filters`)) return;
      if (!(`IntersectionObserver` in window)) return;

      const sentinel = this.previousElementSibling;
      if (!sentinel || !sentinel.hasAttribute(`data-sticky-filters-sentinel`)) return;

      const spacer =
        this.nextElementSibling && this.nextElementSibling.hasAttribute(`data-sticky-filters-spacer`)
          ? this.nextElementSibling
          : null;

      const headerGroup = document.querySelector(`nss-header-group`);
      const headerHeight = headerGroup ? headerGroup.getBoundingClientRect().height : 0;

      // Tells nss-header.js to skip its scroll-hide behavior so the nav
      // stays visible above this bar all the way down the page, same as
      // the PLP's sticky filters (nss-collection-groups.liquid) — otherwise
      // the header hides on scroll-down while this bar (which tracks the
      // header's own position via --nss-header-group-pos) stays fixed,
      // leaving it floating without the header above it.
      document.body.classList.add(`nss-sticky-filters-active`);

      const observer = new IntersectionObserver(
        (entries) => {
          // isIntersecting alone is ambiguous against a rootMargin-shrunk
          // root: it reads false BOTH once the sentinel has scrolled past
          // the trigger line (should be stuck) AND before it's been
          // reached at all (still below the fold, e.g. on initial page
          // load — this section sits after the hero + highlight-recipes
          // blocks, so the sentinel starts off-screen and would otherwise
          // report "stuck" from the very first frame). boundingClientRect
          // gives the sentinel's actual position, which disambiguates the
          // two: only stuck once it's actually scrolled above the header.
          const stuck = entries[0].boundingClientRect.top <= headerHeight;

          if (stuck) {
            // Measured while still in normal flow (before .is-stuck flips
            // position to fixed below), so this is the space to reserve.
            if (spacer) spacer.style.height = `${this.getBoundingClientRect().height}px`;
            this.classList.add(`is-stuck`);
          } else {
            this.classList.remove(`is-stuck`);
            if (spacer) spacer.style.height = ``;
          }

          // Suppresses <nss-header>'s own shadow (nss-header.scss) while
          // this bar is docked directly beneath it, so the combined header
          // + filters bar reads as one shadowed unit instead of two
          // stacked shadows with a seam between them — same technique as
          // the PLP's sticky filters.
          document.body.classList.toggle(`nss-filters-docked`, stuck);
        },
        { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 }
      );
      observer.observe(sentinel);
    }

    handleClick(e) {
      const link = e.target.closest(`.nss-blog-filters__pill`);
      if (!link) return;

      e.preventDefault();
      this.applyFilter(link, true);
    }

    handlePopState() {
      const path = window.location.pathname;
      const link = this.querySelector(`.nss-blog-filters__pill[href="${path}"]`);
      if (link) this.applyFilter(link, false);
    }

    async applyFilter(link, pushHistory) {
      if (this.isLoading) return;
      this.isLoading = true;

      const grid = document.querySelector(`nss-blog`);
      // Online Store 2.0 JSON template sections get a computed id (e.g.
      // "template--xxxxx__main"), not the plain JSON key — read the real
      // value straight off the rendered element instead of guessing it.
      const sectionId = grid ? grid.getAttribute(`data-section-id`) : null;
      if (grid) grid.style.opacity = `0.5`;

      this.querySelectorAll(`.nss-blog-filters__pill`).forEach((pill) => {
        pill.classList.toggle(`nss-blog-filters__pill--active`, pill === link);
      });

      const url = link.getAttribute(`href`);

      if (!sectionId) {
        window.location.href = url;
        return;
      }

      try {
        const sectionUrl = `${url}${url.includes(`?`) ? `&` : `?`}section_id=${sectionId}`;
        const markup = await (await fetch(sectionUrl)).text();
        const dom = new DOMParser().parseFromString(markup, `text/html`);
        const newGrid = dom.querySelector(`nss-blog`);

        if (newGrid && grid) {
          grid.replaceWith(newGrid);
        }

        if (pushHistory && history.pushState) {
          window.history.pushState({}, ``, url);
        }
      } catch (err) {
        if (this.debug) console.log(`[NSS][Blog Filters] Fetch failed, falling back to navigation.`, err);
        window.location.href = url;
        return;
      } finally {
        this.isLoading = false;
        const freshGrid = document.querySelector(`nss-blog`);
        if (freshGrid) freshGrid.style.opacity = ``;
      }
    }
  }

  customElements.define(elementName, NSSBlogFilters);
}
